import { makeStyles } from "@material-ui/core";
import { Button } from "@resource/atlas";
import coderPadLogo from "icons/coderpad-atlas.svg";
import codeSignalLogo from "icons/codesignal-atlas.svg";
import hangoutsLogo from "icons/google-hangouts.svg";
import meetLogo from "icons/meet-atlas.svg";
import teamsLogo from "icons/microsoft-teams.svg";
import zoomLogo from "icons/zoom-atlas.svg";
import isUrl from "is-url";
import Image from "next/image";
import { ResourceCustomTheme } from "styles/config";

const phoneRegex =
  /^.*[\\+]?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}.*$/im;

const useStyles = makeStyles<ResourceCustomTheme>(() => ({
  linkIcon: {
    fontSize: 32,
    maxWidth: 44,
    maxHeight: 44,
    verticalAlign: "middle",
    border: "1px solid rgba(0, 0, 0, 0.05) !important",
    borderRadius: "8px",
  },
}));

type ProminentInstructionsCardProps = {
  label: string;
  url: string | null;
};

enum InstructionsType {
  Hangouts,
  Meet,
  Zoom,
  Teams,
  CoderPad,
  CodeSignal,
  HackerRank,
  PhoneNumber,
  URL,
  None,
}

export const getType = ({
  url,
  label,
}: ProminentInstructionsCardProps): InstructionsType => {
  if (url?.includes("zoom")) return InstructionsType.Zoom;
  if (url?.includes("hangouts.google.com")) return InstructionsType.Hangouts;
  if (url?.includes("meet.google.com")) return InstructionsType.Meet;
  if (url?.includes("teams.microsoft.com")) return InstructionsType.Teams;
  if (url?.includes("coderpad.io")) return InstructionsType.CoderPad;
  if (url?.includes("codesignal.com")) return InstructionsType.CodeSignal;
  if (url?.includes("hackerrank")) return InstructionsType.HackerRank;
  if (label.match(phoneRegex)?.length) return InstructionsType.PhoneNumber;
  if (isUrl(label)) return InstructionsType.URL;
  return InstructionsType.None;
};

export function ProminentInstructionsIcon({
  type,
  className = "",
}: {
  type: InstructionsType;
  className?: string;
}) {
  switch (type) {
    case InstructionsType.Hangouts:
      return (
        <Image
          alt=""
          className={className}
          src={hangoutsLogo}
          layout="fixed"
          objectFit="contain"
          width="44px"
          height="44px"
        />
      );
    case InstructionsType.Zoom:
      return (
        <Image
          alt=""
          className={className}
          src={zoomLogo}
          layout="fixed"
          width="44px"
          height="44px"
          objectFit="contain"
        />
      );
    case InstructionsType.Meet:
      return (
        <Image
          alt=""
          className={className}
          src={meetLogo}
          layout="fixed"
          width="44px"
          height="44px"
          objectFit="contain"
        />
      );
    case InstructionsType.Teams:
      return (
        <Image
          alt=""
          className={className}
          src={teamsLogo}
          layout="fixed"
          width="44px"
          height="44px"
          objectFit="contain"
        />
      );
    case InstructionsType.CoderPad:
      return (
        <Image
          alt=""
          className={className}
          src={coderPadLogo}
          layout="fixed"
          width="44px"
          height="44px"
          objectFit="contain"
        />
      );
    case InstructionsType.CodeSignal:
      return (
        <Image
          alt=""
          className={className}
          src={codeSignalLogo}
          layout="fixed"
          width="44px"
          height="44px"
          objectFit="contain"
        />
      );
    case InstructionsType.HackerRank:
      return (
        <span className={className} role="img" aria-label="Laptop Image">
          💻
        </span>
      );
    case InstructionsType.PhoneNumber:
      return (
        <span className={className} role="img" aria-label="Phone Image">
          ☎️
        </span>
      );
    case InstructionsType.URL:
      return (
        <span className={className} role="img" aria-label="Hyperlink Image">
          🔗
        </span>
      );
    default:
      return (
        <span className={className} role="img" aria-label="Information Image">
          ℹ️
        </span>
      );
  }
}

type LabelProps = {
  label: string;
  type: InstructionsType;
};

export const getLabel = ({ label, type }: LabelProps): string => {
  switch (type) {
    case InstructionsType.Zoom:
      return "Zoom";
    case InstructionsType.Teams:
      return "Microsoft Teams";
    case InstructionsType.Meet:
      return "Google Meet";
    case InstructionsType.CoderPad:
      return "CoderPad";
    case InstructionsType.CodeSignal:
      return "CodeSignal";
    case InstructionsType.HackerRank:
      return "HackerRank";
    default:
      return label;
  }
};

export const getCtaText = (type: InstructionsType): string => {
  switch (type) {
    case InstructionsType.Teams:
    case InstructionsType.Zoom:
    case InstructionsType.Meet:
      return "Join";
    default:
      return "View";
  }
};

function ProminentInstructionsCard({
  label,
  url,
}: ProminentInstructionsCardProps) {
  const classes = useStyles();
  const linkify = isUrl(url || "");
  const type = getType({ label, url });

  return (
    <div className="flex items-center gap-x-3">
      <h3 className="flex margin-0">
        <ProminentInstructionsIcon type={type} className={classes.linkIcon} />
      </h3>
      <div className="overflow-hidden flex-grow text-sm">
        <p className="truncate font-bold">{getLabel({ label, type })}</p>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block truncate text-subtle hover:text-gray-600 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {url}
          </a>
        )}
      </div>
      {linkify && (
        <Button
          as="a"
          href={url ?? undefined}
          target="_blank"
          rel="noreferrer"
          size="small"
          onClick={(event) => event.stopPropagation()}
        >
          {getCtaText(type)}
        </Button>
      )}
    </div>
  );
}

export default ProminentInstructionsCard;
